import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Map as MapIcon, 
  BarChart3, 
  Users, 
  TrendingUp,
  Info
} from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country map centers and zoom levels
const COUNTRY_MAP_CONFIG = {
  NG: { center: [9.082, 8.675], zoom: 6, name: 'Nigeria' },
  GH: { center: [7.946, -1.023], zoom: 7, name: 'Ghana' },
  KE: { center: [-0.023, 37.906], zoom: 6, name: 'Kenya' },
  ZA: { center: [-30.559, 22.937], zoom: 5, name: 'South Africa' },
  EG: { center: [26.820, 30.802], zoom: 6, name: 'Egypt' },
  ET: { center: [9.145, 40.489], zoom: 6, name: 'Ethiopia' },
  TZ: { center: [-6.369, 34.888], zoom: 6, name: 'Tanzania' },
  UG: { center: [1.373, 32.290], zoom: 7, name: 'Uganda' },
  SN: { center: [14.497, -14.452], zoom: 7, name: 'Senegal' },
  CM: { center: [7.369, 12.354], zoom: 6, name: 'Cameroon' },
  CI: { center: [7.539, -5.547], zoom: 7, name: 'Côte d\'Ivoire' },
  MA: { center: [31.791, -7.092], zoom: 6, name: 'Morocco' },
  DZ: { center: [28.033, 1.659], zoom: 5, name: 'Algeria' },
  RW: { center: [-1.940, 29.873], zoom: 8, name: 'Rwanda' }
};

// Nigeria GeoJSON simplified coordinates for states
const NIGERIA_STATES_COORDS = {
  "Abia": [7.5, 5.5], "Adamawa": [12.5, 9.3], "Akwa Ibom": [7.9, 4.9],
  "Anambra": [7.0, 6.2], "Bauchi": [10.3, 10.3], "Bayelsa": [6.1, 4.8],
  "Benue": [8.8, 7.3], "Borno": [13.2, 11.8], "Cross River": [8.3, 5.9],
  "Delta": [5.9, 5.5], "Ebonyi": [8.1, 6.3], "Edo": [5.6, 6.5],
  "Ekiti": [5.2, 7.6], "Enugu": [7.5, 6.4], "FCT": [7.5, 9.1],
  "Gombe": [11.2, 10.3], "Imo": [7.0, 5.5], "Jigawa": [9.4, 12.2],
  "Kaduna": [7.4, 10.5], "Kano": [8.5, 12.0], "Katsina": [7.6, 13.0],
  "Kebbi": [4.2, 12.5], "Kogi": [6.7, 7.8], "Kwara": [4.5, 8.5],
  "Lagos": [3.4, 6.5], "Nasarawa": [8.5, 8.5], "Niger": [6.0, 10.0],
  "Ogun": [3.3, 7.0], "Ondo": [5.0, 7.3], "Osun": [4.5, 7.5],
  "Oyo": [4.0, 8.0], "Plateau": [9.2, 9.0], "Rivers": [7.0, 4.8],
  "Sokoto": [5.2, 13.1], "Taraba": [11.0, 8.0], "Yobe": [11.7, 12.0],
  "Zamfara": [6.3, 12.2]
};

export default function CountryMap() {
  const { country, countryConfig } = useCountry();
  const { language } = useLanguage();
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState('');
  const [mapData, setMapData] = useState({});
  const [selectedState, setSelectedState] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    fetchSurveys();
    fetchMapData();
    loadLeaflet();
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      fetchMapData(selectedSurvey);
    } else {
      fetchMapData();
    }
  }, [selectedSurvey]);

  useEffect(() => {
    if (mapInstanceRef.current && Object.keys(mapData).length > 0) {
      updateMapMarkers();
    }
  }, [mapData]);

  const loadLeaflet = async () => {
    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }
  };

  // Get map config for selected country
  const mapConfig = COUNTRY_MAP_CONFIG[country] || COUNTRY_MAP_CONFIG.NG;
  const countryName = countryConfig?.name || mapConfig.name || 'Nigeria';

  const initMap = () => {
    if (mapInstanceRef.current || !mapRef.current || !window.L) return;

    const L = window.L;
    
    // Initialize map centered on selected country
    const map = L.map(mapRef.current, {
      center: mapConfig.center,
      zoom: mapConfig.zoom,
      minZoom: 4,
      maxZoom: 12,
      scrollWheelZoom: true
    });

    // Add tile layer (light style)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20
    }).addTo(map);

    mapInstanceRef.current = map;
    setLoading(false);
    
    if (Object.keys(mapData).length > 0) {
      updateMapMarkers();
    }
  };

  // Re-center map when country changes
  useEffect(() => {
    if (mapInstanceRef.current && mapConfig) {
      mapInstanceRef.current.setView(mapConfig.center, mapConfig.zoom);
    }
  }, [country]);

  const fetchSurveys = async () => {
    try {
      const response = await fetch(`${API}/surveys`);
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchMapData = async (surveyId = null) => {
    try {
      const url = surveyId 
        ? `${API}/analytics/map-data?survey_id=${surveyId}`
        : `${API}/analytics/map-data`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setMapData(data);
      }
    } catch (error) {
      console.error('Error fetching map data:', error);
    }
  };

  const getIntensityColor = (count) => {
    if (count === 0) return '#E5E7EB';
    if (count < 10) return '#BBF7D0';
    if (count < 50) return '#86EFAC';
    if (count < 100) return '#22C55E';
    return '#006538';
  };

  const updateMapMarkers = () => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers for each state with data
    Object.entries(mapData).forEach(([state, data]) => {
      const coords = NIGERIA_STATES_COORDS[state];
      if (!coords) return;

      const color = getIntensityColor(data.count);
      
      // Create circle marker
      const marker = L.circleMarker([coords[1], coords[0]], {
        radius: Math.min(30, Math.max(10, Math.sqrt(data.count) * 3)),
        fillColor: color,
        color: '#006538',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8
      });

      // Add popup
      const dominantSentiment = data.positive > data.negative 
        ? 'Positive' 
        : data.negative > data.positive 
          ? 'Negative' 
          : 'Neutral';

      marker.bindPopup(`
        <div style="min-width: 150px;">
          <h3 style="font-weight: bold; margin-bottom: 8px;">${state}</h3>
          <p style="margin: 4px 0;"><strong>${data.count}</strong> responses</p>
          <p style="margin: 4px 0;">Sentiment: <strong>${dominantSentiment}</strong></p>
          <div style="margin-top: 8px; font-size: 12px;">
            <span style="color: #059669;">+${data.positive}</span> / 
            <span style="color: #6B7280;">${data.neutral}</span> / 
            <span style="color: #DC2626;">-${data.negative}</span>
          </div>
        </div>
      `);

      marker.on('click', () => {
        setSelectedState({ name: state, ...data });
      });

      marker.addTo(mapInstanceRef.current);
      markersRef.current.push(marker);
    });
  };

  const totalResponses = Object.values(mapData).reduce((sum, d) => sum + d.count, 0);
  const totalPositive = Object.values(mapData).reduce((sum, d) => sum + d.positive, 0);
  const totalNegative = Object.values(mapData).reduce((sum, d) => sum + d.negative, 0);
  const totalNeutral = Object.values(mapData).reduce((sum, d) => sum + d.neutral, 0);

  return (
    <div className="min-h-screen bg-background" data-testid="nigeria-map">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Retour' : 'Back'}
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <h1 className="font-semibold flex items-center gap-2">
                <MapIcon className="h-5 w-5" />
                {language === 'fr' ? `Carte de ${countryName}` : `${countryName} Sentiment Map`}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedSurvey || "all"} onValueChange={(val) => setSelectedSurvey(val === "all" ? "" : val)}>
                <SelectTrigger className="w-64" data-testid="map-survey-select">
                  <SelectValue placeholder={language === 'fr' ? 'Toutes les enquêtes' : 'All Surveys'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes les enquêtes' : 'All Surveys'}</SelectItem>
                  {surveys.map((survey) => (
                    <SelectItem key={survey.survey_id} value={survey.survey_id}>
                      {survey.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link to="/analytics">
                <Button variant="outline" size="sm">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Analyses' : 'Analytics'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Map */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-0">
                <div 
                  ref={mapRef} 
                  className="w-full h-[600px] rounded-lg overflow-hidden"
                  style={{ zIndex: 0 }}
                ></div>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card className="mt-4">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Response intensity:</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#BBF7D0' }}></div>
                      <span className="text-xs">1-9</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#86EFAC' }}></div>
                      <span className="text-xs">10-49</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#22C55E' }}></div>
                      <span className="text-xs">50-99</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#006538' }}></div>
                      <span className="text-xs">100+</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Overview Stats */}
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Aperçu' : 'Overview'}</CardTitle>
                <CardDescription>{countryName}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Total Réponses' : 'Total Responses'}
                  </span>
                  <span className="font-bold text-lg">{totalResponses.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {language === 'fr' ? `${countryConfig?.stats?.level1_label || 'États'} Actifs` : `${countryConfig?.stats?.level1_label || 'States'} Active`}
                  </span>
                  <span className="font-bold text-lg">{Object.keys(mapData).length}</span>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      Positive
                    </span>
                    <span>{totalPositive}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                      Neutral
                    </span>
                    <span>{totalNeutral}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      Negative
                    </span>
                    <span>{totalNegative}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selected State Details */}
            {selectedState && (
              <Card>
                <CardHeader>
                  <CardTitle>{selectedState.name}</CardTitle>
                  <CardDescription>
                    {countryConfig?.stats?.level1_label?.replace(/s$/, '') || 'Region'} {language === 'fr' ? 'Détails' : 'Details'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Responses</span>
                    <span className="font-bold">{selectedState.count}</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Positive</span>
                      <span className="text-green-600">{selectedState.positive}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${selectedState.count > 0 ? (selectedState.positive / selectedState.count) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Neutral</span>
                      <span className="text-gray-600">{selectedState.neutral}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-gray-400 h-2 rounded-full" 
                        style={{ width: `${selectedState.count > 0 ? (selectedState.neutral / selectedState.count) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Negative</span>
                      <span className="text-red-600">{selectedState.negative}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full" 
                        style={{ width: `${selectedState.count > 0 ? (selectedState.negative / selectedState.count) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {selectedState.lgas && Object.keys(selectedState.lgas).length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-sm mb-2">Top LGAs</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedState.lgas)
                          .sort((a, b) => b[1].count - a[1].count)
                          .slice(0, 5)
                          .map(([lga, data]) => (
                            <div key={lga} className="flex items-center justify-between text-sm">
                              <span className="truncate max-w-[150px]">{lga}</span>
                              <Badge variant="outline">{data.count}</Badge>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Top States */}
            <Card>
              <CardHeader>
                <CardTitle>Top Responding States</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(mapData)
                    .sort((a, b) => b[1].count - a[1].count)
                    .slice(0, 5)
                    .map(([state, data], index) => (
                      <div 
                        key={state} 
                        className="flex items-center justify-between p-2 rounded hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => setSelectedState({ name: state, ...data })}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                            {index + 1}
                          </span>
                          <span className="font-medium">{state}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">{data.count}</span>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
